import React, { useContext, useEffect, useMemo } from "react";
import { LeedflowLogoLight as logo } from '@leedpulse-monorepo/assets';
import { LeedflowLogoMinimalist as logoLeedflowCollable } from '@leedpulse-monorepo/assets';
import { Avatar, Button, Divider, Dropdown, Flex, Modal, Select, theme } from "antd";
import { DashboardMenu as LeedDashboardMenu, DashboardMenuItem } from '@leedpulse-monorepo/components';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppContext from "../../contexts/data/AppContext";
import { FileTextOutlined, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { userRoleAsString } from '@leedpulse-monorepo/enums';
import AuthenticationContext from "../../contexts/auth/AuthenticationContext";
import { EThemeNames } from "../../../domain/types/themePresets";
import { useTheme } from "../../contexts/ui/ThemeContext";
import { navigationConfig, NavigationItem } from "../../AppRouter";

import styles from "./DashboardMenu.module.css";

const { useToken } = theme;

interface DashboardMenuProps {
  show: boolean;
  isCollapsed: boolean;
  onNavigate: () => void;
  onCollapseChange: (isCollapsed: boolean) => void;
  onAdminsOpen: (tab?: string) => void;
  onLogsOpen: (id?: string) => void;
}

const DashboardMenu: React.FC<DashboardMenuProps> = ({
  show,
  isCollapsed,
  onNavigate,
  onCollapseChange,
  onAdminsOpen,
  onLogsOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams,] = useSearchParams();

  const { token } = useToken();

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthenticationContext);
  const { themeName, setThemeName } = useTheme();

  const activeKey =
    navigationConfig
      .flatMap((item) => [
        item.path,
        ...(item.children?.map((child) => child.path) ?? []),
      ])
      .find((path) => location.pathname.startsWith(path)) ?? "";

  const isVisible = (item: NavigationItem): boolean => {
    if (item.allowedRoles && (!authContext.authentication || !item.allowedRoles.includes(authContext.authentication.role))) return false;
    if (!item.requiredRole) return true;
    return authContext.authentication?.role === item.requiredRole;
  };

  const toMenuItem = (item: NavigationItem): DashboardMenuItem => {
    const children = item.children?.filter(isVisible).map(toMenuItem);
    return {
      key: item.path,
      title: item.title,
      icon: item.icon,
      iconActive: item.iconActive,
      href: item.path,
      onClick: () => navigate(item.path),
      children: children?.length ? children : undefined,
    };
  };

  const items = useMemo(
    () => navigationConfig.filter(isVisible).map(toMenuItem),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authContext.authentication?.role]
  );

  const footerItems: DashboardMenuItem[] = [
    {
      key: '/admins',
      title: 'Admins',
      icon: <SettingOutlined />,
      onClick: () => onAdminsOpen(),
    },
    {
      key: '/logs',
      title: 'Logs',
      icon: <FileTextOutlined />,
      onClick: () => onLogsOpen(),
    },
  ];

  const modalInfo = () => {
    Modal.info({
      title: 'Informations',
      maskClosable: true,
      content: (
        <div>
          <p>
            <b>Nom :</b> {appContext.item?.app_name}
          </p>
          <p>
            <b>Environnement :</b> {appContext.item?.env}
          </p>
          <p>
            <b>ImapStatus :</b> {appContext.item?.imapStatus}
          </p>
          <p>
            <b>IpV4 :</b> {JSON.stringify(appContext.item?.ipV4)}
          </p>
          <p>
            <b>Interface réseau :</b>{' '}
            {JSON.stringify(appContext.item?.networkInterfaces)}
          </p>
          <p>
            <b>Version :</b> {appContext.item?.version}
          </p>
        </div>
      ),
    });
  };

  const buildVersion = () => {
    const sBuildDate = appContext.item?.buildDate;
    if (sBuildDate) {
      const sDate = sBuildDate.split('T')[0];
      const sTime = sBuildDate.split('T')[1];
      const sYear = sDate.split('-')[0].slice(2, 4);
      const sMonth = sDate.split('-')[1];
      const sDay = sDate.split('-')[2];
      const sHours = sTime.split(':')[0];
      const sMinutes = sTime.split(':')[1];
      return `${sYear}${sMonth}${sDay}.${sHours}.${sMinutes}`;
    }
  };

  useEffect(() => {
    const openAdmins = searchParams.get('open_admins');
    if (openAdmins) {
      onAdminsOpen(openAdmins);
      return;
    }

    const openLogId = searchParams.get('open_logs');
    if (openLogId) {
      onLogsOpen(openLogId);
      return;
    }
  }, [searchParams]);

  return (
    <LeedDashboardMenu
      items={items}
      selectedKey={activeKey}
      show={show}
      isCollapsed={isCollapsed}
      onCollapseChange={onCollapseChange}
      onNavigate={onNavigate}
      logo={logo}
      logoCollapsed={logoLeedflowCollable}
      onLogoClick={modalInfo}
      version={buildVersion()}
      navigationTitle="Menu"
      headerExtra={
        <Dropdown
          trigger={['hover']}
          dropdownRender={() => (
            <div
              className={styles.dropdownContent}
              style={{
                backgroundColor: token.colorBgElevated,
                borderRadius: token.borderRadiusLG,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <div
                className={styles.dropdownHeader}
                style={{
                  color: token.colorTextSecondary,
                }}
              >
                <span
                  className={styles.dropdownTitle}
                  style={{
                    color: token.colorPrimary,
                  }}
                >
                  {authContext.authentication === null
                    ? 'Non connecté'
                    : userRoleAsString(authContext.authentication.role)}
                </span>
                <span className={styles.dropdownSubtitle}>Connecté en tant que</span>
                <span className={styles.dropdownEmail}>
                  {authContext.authentication?.email ?? 'unknown'}
                </span>
              </div>

              <Divider style={{ margin: 0 }} />
              <Flex justify="space-between" align="center" gap={16} style={{ padding: 8 }}>
                <Select
                  value={themeName}
                  options={Object.values(EThemeNames).map(theme => ({ label: theme, value: theme }))}
                  style={{ width: '100%' }}
                  onChange={(value) => setThemeName(value)}
                />
              </Flex>
              <Divider style={{ margin: 0 }} />
              <div className={styles.dropdownFooter}>
                <Button
                  type="primary"
                  className={styles.signOutButton}
                  onClick={() => authContext.submitSignOut()}
                >
                  Se déconnecter
                </Button>
              </div>
            </div>
          )}
        >
          <div className={styles.avatarContainer}>
            <Avatar size={34} icon={<UserOutlined color={token.colorPrimary} />} className={styles.avatar} />
          </div>
        </Dropdown>
      }
      footerItems={footerItems}
    />
  );
};

export default DashboardMenu;
